import { FX_BODY_EFFECT_TYPES, type FxBlock } from "./fxTypes";

export type FxCatalogRenderPath = "cpu-transform" | "gpu-module";

export type FxCatalogEntry = {
  id: string;
  category: FxBlock["category"];
  label: string;
  runtimeProfile: string;
  renderPath: FxCatalogRenderPath;
  gpuModuleId?: string;
};

export type FxCatalogIntegrityIssue = {
  kind:
    | "duplicate-id-mismatch"
    | "duplicate-runtime-profile"
    | "duplicate-gpu-module"
    | "gpu-module-missing-id"
    | "cpu-transform-with-gpu-module";
  message: string;
  entryIds: string[];
};

/**
 * Explicit whitelist for allowed "different effect id -> same gpuModuleId" sharing.
 * Keep empty unless there is an intentional, reviewed exception.
 */
export const FX_GPU_MODULE_SHARE_WHITELIST: Readonly<Record<string, readonly string[]>> = Object.freeze({});

const make = (
  id: string,
  category: FxBlock["category"],
  runtimeProfile: string,
  renderPath: FxCatalogRenderPath = "cpu-transform",
  gpuModuleId?: string
): FxCatalogEntry => ({
  id,
  category,
  label: id,
  runtimeProfile,
  renderPath,
  gpuModuleId,
});

const BODY_ENTRIES: FxCatalogEntry[] = FX_BODY_EFFECT_TYPES.map((id) =>
  make(id, "body", id)
);

export const FX_EFFECT_CATALOG: FxCatalogEntry[] = [
  make("light-leak-sweep", "filter", "light-leak-sweep", "gpu-module", "gpu.lightLeakSweep"),
  make("film-burn", "filter", "film-burn", "gpu-module", "gpu.filmBurn"),
  make("ghost-trail-blur", "filter", "ghost-trail-blur", "gpu-module", "gpu.ghostTrailBlur"),
  make("halation-glow", "filter", "halation-glow", "gpu-module", "gpu.halation"),
  make("anamorphic-streak", "filter", "anamorphic-streak", "gpu-module", "gpu.anamorphicStreak"),
  make("cinematic-bloom-threshold", "filter", "cinematic-bloom-threshold", "gpu-module", "gpu.bloomThreshold"),
  make("soft-film-fade", "filter", "soft-film-fade", "gpu-module", "gpu.softFilmFade"),
  make("film-grain", "filter", "film-grain", "gpu-module", "gpu.filmGrain"),
  make("lens-dust", "filter", "lens-dust", "gpu-module", "gpu.lensDust"),
  make("cinematic-haze", "filter", "cinematic-haze", "gpu-module", "gpu.haze"),
  make("bleach-bypass", "filter", "bleach-bypass", "gpu-module", "gpu.bleachBypass"),
  make("chromatic-aberration-soft", "filter", "chromatic-aberration-soft", "gpu-module", "gpu.chromaticAberration"),
  make("contrast-bloom", "filter", "contrast-bloom", "gpu-module", "gpu.contrastBloom"),
  make("dream-glow", "filter", "dream-glow", "gpu-module", "gpu.dreamGlow"),
  make("chrome-glow", "filter", "chrome-glow", "gpu-module", "gpu.chromeGlow"),
  make("kodak-lut-pack", "filter", "kodak-lut-pack", "gpu-module", "gpu.kodakLutPack"),

  make("light-leak-sweep", "overlay", "light-leak-sweep", "gpu-module", "gpu.lightLeakSweep"),
  make("zoom-pulse", "overlay", "zoom-pulse"),
  make("drift", "overlay", "drift"),
  make("dust-overlay", "overlay", "dust-overlay", "gpu-module", "gpu.dustOverlay"),
  make("bokeh-flares", "overlay", "bokeh-flares", "gpu-module", "gpu.bokehFlares"),
  make("fog-roll", "overlay", "fog-roll", "gpu-module", "gpu.fogRoll"),
  make("rain-streaks", "overlay", "rain-streaks", "gpu-module", "gpu.rainStreaks"),
  make("snow-fall", "overlay", "snow-fall", "gpu-module", "gpu.snow"),
  make("floating-embers", "overlay", "floating-embers", "gpu-module", "gpu.floatingEmbers"),
  make("water-droplets", "overlay", "water-droplets", "gpu-module", "gpu.waterDroplets"),
  make("heat-distortion", "overlay", "heat-distortion", "gpu-module", "gpu.heatDistortion"),
  make("confetti-burst", "overlay", "confetti-burst", "gpu-module", "gpu.confettiBurst"),
  make("fire-sparks", "overlay", "fire-sparks", "gpu-module", "gpu.fireSparks"),
  make("film-scratches", "overlay", "film-scratches", "gpu-module", "gpu.scratches"),
  make("vignette-pulse", "overlay", "vignette-pulse", "gpu-module", "gpu.vignette"),
  make("light-leak-burst", "overlay", "light-leak-burst", "gpu-module", "gpu.lightLeakBurst"),
  make("sparkle-dust", "overlay", "sparkle-dust", "gpu-module", "gpu.sparkleDust"),
  make("smoke-drift", "overlay", "smoke-drift", "gpu-module", "gpu.smokeDrift"),
  make("fog-depth-layer", "overlay", "fog-depth-layer", "gpu-module", "gpu.fogDepth"),

  make("shake", "distortion", "shake"),
  make("camera-shake-pro", "distortion", "camera-shake-pro"),
  make("flicker", "distortion", "flicker"),
  make("film-gate-jitter", "distortion", "film-gate-jitter"),
  make("drift", "distortion", "drift"),
  make("data-moshing", "distortion", "data-moshing", "gpu-module", "gpu.dataMosh"),
  make("screen-crack-impact", "distortion", "screen-crack-impact", "gpu-module", "gpu.screenCrack"),
  make("speed-ramp-blur", "distortion", "speed-ramp-blur", "gpu-module", "gpu.speedRampBlur"),
  make("flash-white-pro", "distortion", "flash-white-pro", "gpu-module", "gpu.flashWhitePro"),
  make(
    "directional-motion-blur-pro",
    "distortion",
    "directional-motion-blur-pro",
    "gpu-module",
    "gpu.directionalMotionBlur"
  ),
  make("glitch-rgb", "distortion", "glitch-rgb", "gpu-module", "gpu.glitchRgb"),
  make("glitch-frame-tear", "distortion", "glitch-frame-tear", "gpu-module", "gpu.glitchTear"),
  make("ripple-warp", "distortion", "ripple-warp", "gpu-module", "gpu.rippleWarp"),
  make("zoom-punch", "distortion", "zoom-punch"),
  make("motion-blur-x", "distortion", "motion-blur-x", "gpu-module", "gpu.motionBlurX"),
  make("motion-blur-y", "distortion", "motion-blur-y", "gpu-module", "gpu.motionBlurY"),
  make("barrel-warp", "distortion", "barrel-warp", "gpu-module", "gpu.barrel"),
  make("twirl-warp", "distortion", "twirl-warp", "gpu-module", "gpu.twirl"),
  make("scanline-jitter", "distortion", "scanline-jitter", "gpu-module", "gpu.scanlineJitter"),
  make("shockwave", "distortion", "shockwave", "gpu-module", "gpu.shockwave"),
  make("chromatic-shift", "distortion", "chromatic-shift", "gpu-module", "gpu.chromaticShift"),

  ...BODY_ENTRIES,

  make("glow-pulse", "audio-reactive", "glow-pulse"),
  make("zoom-pulse", "audio-reactive", "zoom-pulse"),
  make("beat-zoom-micro", "audio-reactive", "beat-zoom-micro"),
  make("flicker", "audio-reactive", "flicker"),
  make("bar-flash-overlay", "audio-reactive", "bar-flash-overlay", "gpu-module", "gpu.barFlash"),
  make("beat-ripple", "audio-reactive", "beat-ripple"),
  make("bass-shake", "audio-reactive", "bass-shake"),
  make("bass-zoom", "audio-reactive", "bass-zoom"),
  make("transient-shake", "audio-reactive", "transient-shake"),
  make("onset-flash", "audio-reactive", "onset-flash"),
  make("impact-flash", "audio-reactive", "impact-flash", "gpu-module", "gpu.impactFlash"),
  make("impact-shockwave", "audio-reactive", "impact-shockwave", "gpu-module", "gpu.impactShockwave"),
  make("beat-scanline", "audio-reactive", "beat-scanline", "gpu-module", "gpu.beatScanline"),
  make("tempo-ripple", "audio-reactive", "tempo-ripple", "gpu-module", "gpu.tempoRipple"),
  make("beat-rgb-pulse", "audio-reactive", "beat-rgb-pulse", "gpu-module", "gpu.beatRgbPulse"),
  make("beat-particle-burst", "audio-reactive", "beat-particle-burst", "gpu-module", "gpu.beatParticleBurst"),
  make("beat-light-leak", "audio-reactive", "beat-light-leak", "gpu-module", "gpu.beatLightLeak"),
  make("beat-outline-flicker", "audio-reactive", "beat-outline-flicker"),
  make("treble-sparkle", "audio-reactive", "treble-sparkle", "gpu-module", "gpu.trebleSparkle"),
];

export function isFxGpuModuleShareWhitelisted(args: { moduleId: string; effectIds: Iterable<string> }): boolean {
  const expected = FX_GPU_MODULE_SHARE_WHITELIST[args.moduleId];
  if (!expected || expected.length === 0) return false;
  const actualIds = [...new Set(Array.from(args.effectIds).map((id) => id.toLowerCase()))].sort();
  const expectedIds = [...new Set(expected.map((id) => id.toLowerCase()))].sort();
  if (actualIds.length !== expectedIds.length) return false;
  for (let i = 0; i < actualIds.length; i += 1) {
    if (actualIds[i] !== expectedIds[i]) return false;
  }
  return true;
}

export function collectFxCatalogIntegrityIssues(entries: readonly FxCatalogEntry[]): FxCatalogIntegrityIssue[] {
  const issues: FxCatalogIntegrityIssue[] = [];
  const byId = new Map<string, FxCatalogEntry[]>();
  const byRuntime = new Map<string, Set<string>>();
  const byModule = new Map<string, Set<string>>();

  for (const entry of entries) {
    const id = entry.id.toLowerCase();
    const row = byId.get(id) ?? [];
    row.push(entry);
    byId.set(id, row);

    const runtimeSet = byRuntime.get(entry.runtimeProfile) ?? new Set<string>();
    runtimeSet.add(entry.id);
    byRuntime.set(entry.runtimeProfile, runtimeSet);

    if (entry.gpuModuleId) {
      const moduleSet = byModule.get(entry.gpuModuleId) ?? new Set<string>();
      moduleSet.add(entry.id);
      byModule.set(entry.gpuModuleId, moduleSet);
    }

    if (entry.renderPath === "gpu-module" && !entry.gpuModuleId) {
      issues.push({
        kind: "gpu-module-missing-id",
        message: `FX "${entry.id}" is gpu-module but missing gpuModuleId`,
        entryIds: [entry.id],
      });
    }
    if (entry.renderPath === "cpu-transform" && entry.gpuModuleId) {
      issues.push({
        kind: "cpu-transform-with-gpu-module",
        message: `FX "${entry.id}" is cpu-transform but has gpuModuleId "${entry.gpuModuleId}"`,
        entryIds: [entry.id],
      });
    }
  }

  for (const [id, rows] of byId.entries()) {
    if (rows.length <= 1) continue;
    const first = rows[0];
    const mismatch = rows.some(
      (row) =>
        row.runtimeProfile !== first.runtimeProfile ||
        row.renderPath !== first.renderPath ||
        (row.gpuModuleId ?? null) !== (first.gpuModuleId ?? null)
    );
    if (mismatch) {
      issues.push({
        kind: "duplicate-id-mismatch",
        message: `FX id "${id}" has mismatched wiring across categories`,
        entryIds: [...new Set(rows.map((row) => row.id))],
      });
    }
  }

  for (const [runtimeProfile, ids] of byRuntime.entries()) {
    if (ids.size > 1) {
      issues.push({
        kind: "duplicate-runtime-profile",
        message: `Runtime profile "${runtimeProfile}" is shared by multiple effect ids`,
        entryIds: [...ids],
      });
    }
  }

  for (const [gpuModuleId, ids] of byModule.entries()) {
    if (ids.size > 1 && !isFxGpuModuleShareWhitelisted({ moduleId: gpuModuleId, effectIds: ids })) {
      issues.push({
        kind: "duplicate-gpu-module",
        message: `GPU module "${gpuModuleId}" is shared by multiple effect ids`,
        entryIds: [...ids],
      });
    }
  }

  return issues;
}

const EFFECT_BY_ID = new Map<string, FxCatalogEntry>(
  FX_EFFECT_CATALOG.map((entry) => [entry.id.toLowerCase(), entry])
);
const LEGACY_FILTER_STACK_EFFECT_ID = "legacy-filter-stack";
const LEGACY_FILTER_STACK_MODULE_ID = "gpu.legacyFilterStack";

const EFFECTS_BY_CATEGORY = new Map<FxBlock["category"], string[]>([
  ["filter", []],
  ["overlay", []],
  ["distortion", []],
  ["body", []],
  ["audio-reactive", []],
]);

for (const entry of FX_EFFECT_CATALOG) {
  const list = EFFECTS_BY_CATEGORY.get(entry.category);
  if (!list) continue;
  if (!list.includes(entry.id)) list.push(entry.id);
}

for (const list of EFFECTS_BY_CATEGORY.values()) {
  list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

if (import.meta.env?.DEV) {
  const issues = collectFxCatalogIntegrityIssues(FX_EFFECT_CATALOG);
  if (issues.length > 0) {
    console.error("[FX Catalog] Integrity issues detected", issues);
  }
}

export function getFxEffectOptionsByCategory(category: FxBlock["category"]): readonly string[] {
  return EFFECTS_BY_CATEGORY.get(category) ?? [];
}

export function resolveFxRuntimeProfile(effectType: string): string {
  const normalized = String(effectType || "").trim().toLowerCase();
  if (!normalized) return "drift";
  if (normalized === LEGACY_FILTER_STACK_EFFECT_ID) return LEGACY_FILTER_STACK_EFFECT_ID;
  const fromCatalog = EFFECT_BY_ID.get(normalized);
  return fromCatalog?.runtimeProfile ?? normalized;
}

export function resolveFxGpuModuleId(effectType: string): string | null {
  const normalized = String(effectType || "").trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === LEGACY_FILTER_STACK_EFFECT_ID) return LEGACY_FILTER_STACK_MODULE_ID;
  return EFFECT_BY_ID.get(normalized)?.gpuModuleId ?? null;
}

export function resolveFxRenderPath(effectType: string): FxCatalogRenderPath {
  const normalized = String(effectType || "").trim().toLowerCase();
  if (!normalized) return "cpu-transform";
  if (normalized === LEGACY_FILTER_STACK_EFFECT_ID) return "gpu-module";
  return EFFECT_BY_ID.get(normalized)?.renderPath ?? "cpu-transform";
}
